import { uploadUrl } from '../api/api';

// Check if video URL is valid (not pending, not empty, etc.)
export const isValidVideoUrl = (video) => {
  if (!video) return false;
  
  // Convert to string and normalize
  const videoStr = String(video).toLowerCase().trim();
  
  // Check for invalid status indicators anywhere in the string
  const invalidStatuses = ['pending', 'processing', 'uploading', 'failed', 'error'];
  if (invalidStatuses.some(status => videoStr.includes(status))) {
    return false;
  }
  
  // Check if it's just a status word without a file extension or valid path
  if (videoStr === 'pending' || videoStr === 'processing' || videoStr === 'uploading' || 
      videoStr === 'failed' || videoStr === 'error' || videoStr === 'null' || videoStr === 'undefined') {
    return false;
  }
  
  // Check if URL ends with invalid paths
  if (videoStr.endsWith('/pending') || 
      videoStr.endsWith('/processing') || 
      videoStr.endsWith('/uploading') ||
      videoStr.endsWith('/failed') ||
      videoStr.endsWith('/error')) {
    return false;
  }
  
  // Check if it's a valid file path or URL (must have file extension or be a valid URL pattern)
  // Allow HLS playlists, video files, and embed URLs
  const hasValidExtension = /\.(m3u8|mp4|webm|mov|avi|mkv|flv|wmv|mpg|mpeg|ts)$/i.test(videoStr);
  const isEmbedUrl = videoStr.includes('youtube.com') || videoStr.includes('vimeo.com') || videoStr.includes('youtu.be');
  const isHlsPath = videoStr.includes('/playlist.m3u8') || videoStr.includes('.m3u8');
  const isBlobUrl = videoStr.startsWith('blob:');
  const isDataUrl = videoStr.startsWith('data:');
  
  // If it's not a valid file/URL pattern and doesn't have a status indicator, it might still be invalid
  // But we'll allow it if it looks like a path (contains slashes) or is a full URL
  if (!hasValidExtension && !isEmbedUrl && !isHlsPath && !isBlobUrl && !isDataUrl) {
    // If it's a simple word without path indicators, it's likely invalid
    if (!videoStr.includes('/') && !videoStr.includes('http') && !videoStr.includes('\\')) {
      return false;
    }
  }
  
  return true;
};

export const getVideoUrl = (video) => {
  if (!video) return null;
  
  // Validate video URL first
  if (!isValidVideoUrl(video)) {
    return null; // Return null for invalid URLs
  }
  
  // Handle blob URLs - use them directly without modification
  if (video.startsWith('blob:')) {
    return video;
  }
  
  // Handle data URLs - use them directly
  if (video.startsWith('data:')) {
    return video;
  }
  
  // Handle embed URLs
  if (video.includes('youtube.com/embed') || video.includes('vimeo.com/video')) return video;
  
  // Handle YouTube URLs
  if (video.includes('youtube.com') || video.includes('youtu.be')) {
    const youtubeRegex = /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/;
    const youtubeMatch = video.match(youtubeRegex);
    if (youtubeMatch) return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
  }
  
  // Handle Vimeo URLs
  if (video.includes('vimeo.com')) {
    const vimeoRegex = /(?:vimeo\.com\/)(?:.*\/)?(\d+)/;
    const vimeoMatch = video.match(vimeoRegex);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  }
  
  // Handle relative paths - prepend upload URL
  if (!video.startsWith('http')) {
    // For paths with slashes (like videos/{newsId}/playlist.m3u8), encode each segment separately
    // Don't encode the entire path as it will encode slashes incorrectly
    if (video.includes('/')) {
      const pathSegments = video.split('/');
      const encodedPath = pathSegments.map(segment => encodeURIComponent(segment)).join('/');
      return `${uploadUrl}/${encodedPath}`;
    }
    // For simple filenames without slashes, encode the whole thing
    return `${uploadUrl}/${encodeURIComponent(video)}`;
  }
  
  // Return as-is for full HTTP/HTTPS URLs
  return video;
};

export const isEmbedUrl = (video) => {
  if (!video) return false;
  return video.includes('youtube.com/embed') || video.includes('vimeo.com/video');
};

export const isHlsUrl = (url) => {
  if (!url) return false;
  return url.includes('.m3u8') || url.endsWith('.m3u8');
};

// Get podcast URL - handles various podcast platforms
export const getPodcastUrl = (podcastUrl) => {
  if (!podcastUrl) return null;
  
  // Return as-is if already a valid URL
  if (podcastUrl.startsWith('http://') || podcastUrl.startsWith('https://')) {
    return podcastUrl;
  }
  
  return null;
};

// Check if URL is a podcast platform URL
export const isPodcastUrl = (url) => {
  if (!url) return false;
  
  const podcastPlatforms = [
    'spotify.com',
    'open.spotify.com',
    'podcasts.apple.com',
    'podcasts.google.com',
    'anchor.fm',
    'castbox.fm',
    'pocketcasts.com',
    'overcast.fm',
    'stitcher.com',
    'tunein.com',
    'iheart.com',
    'soundcloud.com',
    'instagram.com',
    'facebook.com',
    'twitter.com',
    'x.com',
  ];
  
  const lowerUrl = url.toLowerCase();
  return podcastPlatforms.some(platform => lowerUrl.includes(platform));
};

// Format podcast URL for display
export const formatPodcastUrl = (url) => {
  if (!url) return null;
  
  // Spotify URLs
  if (url.includes('spotify.com') || url.includes('open.spotify.com')) {
    // Extract episode/show ID from Spotify URL
    const spotifyRegex = /spotify\.com\/(?:episode|show|podcast)\/([a-zA-Z0-9]+)/;
    const match = url.match(spotifyRegex);
    if (match) {
      return url; // Return original URL
    }
  }
  
  // Apple Podcasts URLs
  if (url.includes('podcasts.apple.com')) {
    return url; // Return original URL
  }
  
  // Google Podcasts URLs
  if (url.includes('podcasts.google.com')) {
    return url; // Return original URL
  }
  
  // Instagram URLs
  if (url.includes('instagram.com')) {
    // Extract post ID or handle
    const instagramRegex = /instagram\.com\/(?:p|reel|tv)\/([a-zA-Z0-9_-]+)/;
    const match = url.match(instagramRegex);
    if (match) {
      return url; // Return original URL
    }
  }
  
  // Return as-is for other platforms
  return url;
};

